import React, { useCallback, useEffect, useState } from 'react';
import platformService from '../api/platformService';

const formatCurrency = (amount) => `Rp ${Number(amount || 0).toLocaleString('id-ID')}`;

const FEATURE_LABELS = {
  transactions: 'Transactions',
  finance_accounting: 'Finance & Accounting',
  bi_reports: 'BI & Reports',
  ai_assistant: 'AI Assistant',
};

const INFRA_FIELDS = [
  { key: 'storage_mb', label: 'Storage (MB)' },
  { key: 'max_users', label: 'Max Users' },
  { key: 'max_transactions_per_month', label: 'Max Transactions/Month' },
  { key: 'rate_limit_per_min', label: 'Rate Limit (req/min)' },
  { key: 'ai_concurrency', label: 'AI Concurrency' },
];

const emptyForm = {
  code: '',
  name: '',
  price_monthly: 0,
  price_yearly: 0,
  trial_days: 14,
  sort_order: 0,
  is_public: true,
  is_active: true,
  features: { transactions: true, finance_accounting: false, bi_reports: false, ai_assistant: false },
  infra_limits: { storage_mb: 1024, max_users: 3, max_transactions_per_month: 200, rate_limit_per_min: 60, ai_concurrency: 0 },
};

const PlanForm = ({ initial, isEdit, onCancel, onSaved }) => {
  const [form, setForm] = useState(initial);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const setField = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));
  const setFeature = (key, value) => setForm((prev) => ({ ...prev, features: { ...prev.features, [key]: value } }));
  const setInfra = (key, value) => setForm((prev) => ({ ...prev, infra_limits: { ...prev.infra_limits, [key]: value } }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      const payload = {
        ...form,
        price_monthly: Number(form.price_monthly),
        price_yearly: Number(form.price_yearly),
        trial_days: Number(form.trial_days),
        sort_order: Number(form.sort_order),
        infra_limits: Object.fromEntries(
          Object.entries(form.infra_limits).map(([k, v]) => [k, Number(v)])
        ),
      };

      if (isEdit) {
        delete payload.code; // code is immutable after creation
        await platformService.updatePlan(initial._id, payload);
      } else {
        await platformService.createPlan(payload);
      }
      onSaved();
    } catch (err) {
      setError(err.response?.data?.messages?.error || `Failed to ${isEdit ? 'update' : 'create'} plan.`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="card w-full max-w-2xl max-h-[90vh] overflow-y-auto p-6">
        <h2 className="text-lg font-bold text-gray-900 mb-4">{isEdit ? 'Edit Plan' : 'Add Plan'}</h2>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
            <p className="text-red-600 text-sm">{error}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Code</label>
              <input
                className="input-field"
                value={form.code}
                onChange={(e) => setField('code', e.target.value.toLowerCase())}
                disabled={isEdit}
                required
                placeholder="e.g. growth"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
              <input className="input-field" value={form.name} onChange={(e) => setField('name', e.target.value)} required />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Price / Month (IDR)</label>
              <input type="number" min="0" className="input-field" value={form.price_monthly} onChange={(e) => setField('price_monthly', e.target.value)} required />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Price / Year (IDR)</label>
              <input type="number" min="0" className="input-field" value={form.price_yearly} onChange={(e) => setField('price_yearly', e.target.value)} required />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Trial Days</label>
              <input type="number" min="0" className="input-field" value={form.trial_days} onChange={(e) => setField('trial_days', e.target.value)} required />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Sort Order</label>
              <input type="number" min="0" className="input-field" value={form.sort_order} onChange={(e) => setField('sort_order', e.target.value)} />
            </div>
          </div>

          <div className="flex items-center space-x-6">
            <label className="flex items-center space-x-2 text-sm text-gray-700">
              <input type="checkbox" checked={form.is_public} onChange={(e) => setField('is_public', e.target.checked)} />
              <span>Public (shown on /pricing)</span>
            </label>
            <label className="flex items-center space-x-2 text-sm text-gray-700">
              <input type="checkbox" checked={form.is_active} onChange={(e) => setField('is_active', e.target.checked)} />
              <span>Active (selectable for new signups/upgrades)</span>
            </label>
          </div>

          <div>
            <p className="text-sm font-medium text-gray-700 mb-2">Features</p>
            <div className="grid grid-cols-2 gap-2">
              {Object.entries(FEATURE_LABELS).map(([key, label]) => (
                <label key={key} className="flex items-center space-x-2 text-sm text-gray-600">
                  <input type="checkbox" checked={!!form.features[key]} onChange={(e) => setFeature(key, e.target.checked)} />
                  <span>{label}</span>
                </label>
              ))}
            </div>
          </div>

          <div>
            <p className="text-sm font-medium text-gray-700 mb-2">Infrastructure Limits</p>
            <div className="grid grid-cols-2 gap-4">
              {INFRA_FIELDS.map(({ key, label }) => (
                <div key={key}>
                  <label className="block text-xs text-gray-500 mb-1">{label}</label>
                  <input
                    type="number"
                    min="0"
                    className="input-field"
                    value={form.infra_limits[key]}
                    onChange={(e) => setInfra(key, e.target.value)}
                  />
                </div>
              ))}
            </div>
          </div>

          <div className="flex justify-end space-x-3 pt-2">
            <button type="button" className="btn-secondary" onClick={onCancel} disabled={saving}>Cancel</button>
            <button type="submit" className="btn-primary" disabled={saving}>{saving ? 'Saving...' : 'Save Plan'}</button>
          </div>
        </form>
      </div>
    </div>
  );
};

const PlansPage = () => {
  const [plans, setPlans] = useState([]);
  const [error, setError] = useState('');
  const [actionError, setActionError] = useState('');
  const [busyCode, setBusyCode] = useState(null);
  const [modalPlan, setModalPlan] = useState(null); // null = closed, {} = create, {...plan} = edit
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    platformService.getPlans()
      .then((res) => {
        if (res.status === 'success') setPlans(res.data);
        else setError('Failed to load plans.');
      })
      .catch(() => setError('Failed to load plans.'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleToggleActive = async (plan) => {
    setActionError('');
    setBusyCode(plan.code);
    try {
      await platformService.updatePlan(plan._id, { is_active: !plan.is_active });
      load();
    } catch (err) {
      setActionError(err.response?.data?.messages?.error || 'Failed to update plan.');
    } finally {
      setBusyCode(null);
    }
  };

  const handleDelete = async (plan) => {
    if (!window.confirm(`Delete plan "${plan.name}"? This cannot be undone.`)) return;
    setActionError('');
    setBusyCode(plan.code);
    try {
      await platformService.deletePlan(plan._id);
      load();
    } catch (err) {
      setActionError(err.response?.data?.messages?.error || 'Failed to delete plan.');
    } finally {
      setBusyCode(null);
    }
  };

  if (error) return <div className="text-red-600">{error}</div>;

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Plans</h1>
          <p className="text-sm text-gray-500 mt-1">Manage pricing, features, and limits for every plan tier.</p>
        </div>
        <button className="btn-primary" onClick={() => setModalPlan({ ...emptyForm })}>+ Add Plan</button>
      </div>

      {actionError && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
          <p className="text-red-600 text-sm">{actionError}</p>
        </div>
      )}

      {loading ? (
        <p className="text-gray-500">Loading...</p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {plans.map((plan) => (
            <div key={plan.code} className={`card p-6 ${plan.is_active === false ? 'opacity-60' : ''}`}>
              <div className="flex items-start justify-between">
                <h2 className="text-lg font-bold text-gray-900">{plan.name}</h2>
                <div className="flex items-center space-x-1">
                  {plan.is_public ? (
                    <span className="badge bg-blue-50 text-blue-700">public</span>
                  ) : (
                    <span className="badge bg-gray-100 text-gray-600">hidden</span>
                  )}
                  {plan.is_active === false && <span className="badge bg-red-50 text-red-700">disabled</span>}
                </div>
              </div>

              <p className="text-2xl font-bold text-gray-900 mt-2">
                {formatCurrency(plan.price_monthly)}
                <span className="text-sm font-normal text-gray-500">/mo</span>
              </p>
              <p className="text-sm text-gray-500">{formatCurrency(plan.price_yearly)}/yr &middot; {plan.trial_days}-day trial</p>

              <div className="mt-4 space-y-1">
                {Object.entries(FEATURE_LABELS).map(([key, label]) => (
                  <div key={key} className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">{label}</span>
                    <span className={plan.features?.[key] ? 'text-green-600 font-medium' : 'text-gray-300'}>
                      {plan.features?.[key] ? 'Included' : '—'}
                    </span>
                  </div>
                ))}
              </div>

              <div className="mt-4 pt-4 border-t border-gray-100 space-y-1 text-sm text-gray-600">
                <div>Storage: {(plan.infra_limits?.storage_mb / 1024).toFixed(1)} GB</div>
                <div>Users: {plan.infra_limits?.max_users}</div>
                <div>Transactions/mo: {plan.infra_limits?.max_transactions_per_month?.toLocaleString('id-ID')}</div>
                <div>Rate limit: {plan.infra_limits?.rate_limit_per_min}/min</div>
              </div>

              <div className="mt-4 pt-4 border-t border-gray-100 flex items-center justify-between">
                <button
                  className="text-sm text-primary-600 hover:text-primary-500 font-medium"
                  onClick={() => setModalPlan(plan)}
                  disabled={busyCode === plan.code}
                >
                  Edit
                </button>
                <button
                  className="text-sm text-yellow-600 hover:text-yellow-500 font-medium disabled:opacity-50"
                  onClick={() => handleToggleActive(plan)}
                  disabled={busyCode === plan.code}
                >
                  {plan.is_active === false ? 'Enable' : 'Disable'}
                </button>
                <button
                  className="text-sm text-red-600 hover:text-red-500 font-medium disabled:opacity-50"
                  onClick={() => handleDelete(plan)}
                  disabled={busyCode === plan.code}
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {modalPlan && (
        <PlanForm
          initial={modalPlan._id ? modalPlan : emptyForm}
          isEdit={!!modalPlan._id}
          onCancel={() => setModalPlan(null)}
          onSaved={() => { setModalPlan(null); load(); }}
        />
      )}
    </div>
  );
};

export default PlansPage;
